import Button from '@components/elements/button/button';
import ErrorMessage from '@components/elements/error-message/error-message';
import SuccessMessage from '@components/elements/success-message/success-message';
import FormSection from '@components/forms/form-section/form-section';
import InputEnvelope from '@components/forms/input-envelope/input-envelope';
import InputRow from '@components/forms/input-row/input-row';
import Checkbox, {
  fieldToCheckboxProps,
} from '@components/inputs/checkbox/checkbox';
import FileInput, {
  fieldToFileInputProps,
} from '@components/inputs/file-input/file-input';
import InputArea, {
  fieldToInputAreaProps,
} from '@components/inputs/input-area/input-area';
import Input, { fieldToInputProps } from '@components/inputs/input/input';
import Select, {
  fieldToSelectProps,
  SelectOption,
} from '@components/inputs/select/select';
import {
  v2_useAuthorizedCustomer,
  v2_useBranches,
  v2_useContact,
} from '@crocodille/common';
import { useRouter } from '@framework/router';
import { cmsPageRoute } from '@routing';
import { translateError } from '@utils/error';
import Link from 'next/link';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { parse, format } from 'date-fns';

const ContactForm: React.FC = () => {
  const { t } = useTranslation(['common', 'contact']);
  const { query } = useRouter();

  const { data: customer } = v2_useAuthorizedCustomer();
  const {
    form: { fields, handleSubmit, reset },
    isMutating,
    error,
    success,
  } = v2_useContact();
  const { data: branches } = v2_useBranches();

  const preSetBranch = branches?.find((b) => b.code === query.branchCode);

  const formatDate = (dateStr: string | string[] | undefined) => {
    if (typeof dateStr !== 'string') {
      return;
    }

    const parsedDate = parse(dateStr, 'dd.MM.yyyy', new Date());
    return format(parsedDate, 'yyyy-MM-dd');
  };

  useEffect(() => {
    reset({
      email: customer?.email ?? '',
      receiptCode: query.orderNumber as string | undefined,
      branch: preSetBranch?.name,
      dateOfVisit: formatDate(query.date),
      timeOfVisit: query.time as string | undefined,
    });
  }, [
    customer?.email,
    preSetBranch?.name,
    query.date,
    query.orderNumber,
    query.time,
    reset,
  ]);

  return (
    <form
      encType="multipart/form-data"
      style={{ maxWidth: '640px', margin: '0 auto' }}
      onSubmit={(e) => {
        e.preventDefault();
        const valid = handleSubmit();

        if (fields.attachment.error) {
          fields.attachment.onChange(null);
        }

        if (valid) {
          reset({
            email: customer?.email ?? '',
            receiptCode: query.orderNumber as string | undefined,
            branch: preSetBranch?.name,
            dateOfVisit: formatDate(query.date),
            timeOfVisit: query.time as string | undefined,
          });
        }
      }}
    >
      {success ? (
        <FormSection
          title={t('contact:thank_you.title')}
          titlePlacement="above"
        >
          <SuccessMessage>{t('contact:thank_you.message')}</SuccessMessage>
        </FormSection>
      ) : null}
      <FormSection title={''}>
        <InputEnvelope error={translateError(t, fields.receiptCode.error)}>
          <Input
            name="receiptCode"
            label={t('contact:form.receipt_code')}
            {...fieldToInputProps(fields.receiptCode)}
          />
        </InputEnvelope>
        <InputEnvelope error={translateError(t, fields.messageType.error)}>
          <Select
            name="messageType"
            label={t('contact:form.message_type')}
            {...fieldToSelectProps(fields.messageType)}
          >
            <SelectOption
              value={t('contact:message_type.complaint')}
              label={t('contact:message_type.complaint')}
            />
            <SelectOption
              value={t('contact:message_type.praise')}
              label={t('contact:message_type.praise')}
            />
            <SelectOption
              value={t('contact:message_type.question')}
              label={t('contact:message_type.question')}
            />
            <SelectOption
              value={t('contact:message_type.suggestion')}
              label={t('contact:message_type.suggestion')}
            />
          </Select>
        </InputEnvelope>
        <InputEnvelope error={translateError(t, fields.branch.error)}>
          <Select
            name="branch"
            label={t('contact:form.branch')}
            {...fieldToSelectProps(fields.branch)}
          >
            {branches?.map?.((branch) => (
              <SelectOption
                key={branch.code}
                value={branch.name}
                label={branch.name}
              />
            ))}
          </Select>
        </InputEnvelope>
        <InputRow>
          <InputEnvelope error={translateError(t, fields.dateOfVisit.error)}>
            <Input
              name="dateOfVisit"
              type="date"
              label={t('contact:form.date_of_visit')}
              {...fieldToInputProps(fields.dateOfVisit)}
            />
          </InputEnvelope>
          <InputEnvelope error={translateError(t, fields.timeOfVisit.error)}>
            <Input
              name="timeOfVisit"
              type="time"
              label={t('contact:form.time_of_visit')}
              {...fieldToInputProps(fields.timeOfVisit)}
            />
          </InputEnvelope>
        </InputRow>
        <InputEnvelope error={translateError(t, fields.email.error)}>
          <Input
            name="email"
            type="email"
            label={t('contact:form.email')}
            {...fieldToInputProps(fields.email)}
          />
        </InputEnvelope>
        <InputEnvelope
          error={translateError(t, fields.message.error, { maxLength: 1000 })}
        >
          <InputArea
            name="message"
            label={t('contact:form.message')}
            {...fieldToInputAreaProps(fields.message)}
          />
        </InputEnvelope>
        <InputEnvelope
          error={translateError(t, fields.attachment.error, {
            maxSize: 15,
          })}
        >
          <FileInput
            name="attachment"
            clearFile={Boolean(fields.attachment.error)}
            {...fieldToFileInputProps(fields.attachment)}
          />
        </InputEnvelope>
        <InputRow>
          <InputEnvelope error={translateError(t, fields.agreement.error)}>
            <Checkbox
              label={
                <Trans t={t} i18nKey={'contact:form.data_agreement'}>
                  <Link
                    href={cmsPageRoute({ slug: 'terms-and-conditions' })}
                  ></Link>
                </Trans>
              }
              {...fieldToCheckboxProps(fields.agreement)}
            />
          </InputEnvelope>
          {error ? (
            <ErrorMessage>{t('common:forms.generic_error')}</ErrorMessage>
          ) : null}
          <Button type="submit" pending={isMutating}>
            {t('common:forms.send')}
          </Button>
        </InputRow>
      </FormSection>
    </form>
  );
};

export default ContactForm;
